'use client';

import { useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { Building2, GripVertical, User, X } from 'lucide-react';
import type {
  StoreAssignmentBoardStoreItem,
  StoreAssignmentBoardUserItem,
  StoreAssignmentItem,
} from '@/app/api-client/store-assignment-client';

type StoreAssignmentBoardModalProps = {
  isOpen: boolean;
  selectedStore: StoreAssignmentBoardStoreItem | null;
  users: StoreAssignmentBoardUserItem[];
  assignments: StoreAssignmentItem[];
  isSaving: boolean;
  errorMessage: string;
  canDeleteStore: boolean;
  onAssign: (params: { userId: string }) => Promise<void>;
  onUnassign: (userId: string) => Promise<void>;
  onDeleteStore: () => void;
  onClose: () => void;
};

export default function StoreAssignmentBoardModal({
  isOpen,
  selectedStore,
  users,
  assignments,
  isSaving,
  errorMessage,
  canDeleteStore,
  onAssign,
  onUnassign,
  onDeleteStore,
  onClose,
}: StoreAssignmentBoardModalProps) {
  const [draggingUserId, setDraggingUserId] = useState<string | null>(null);

  const assignmentsByUserId = useMemo(() => {
    const map = new Map<string, StoreAssignmentItem[]>();
    assignments.forEach((item) => {
      const current = map.get(item.userId) ?? [];
      map.set(item.userId, [...current, item]);
    });
    return map;
  }, [assignments]);

  const selectedStoreAssignments = useMemo(() => {
    if (!selectedStore) return [];
    return assignments.filter((item) => item.storeId === selectedStore.id);
  }, [assignments, selectedStore]);

  const selectedStoreUserIds = useMemo(
    () => new Set(selectedStoreAssignments.map((item) => item.userId)),
    [selectedStoreAssignments],
  );

  const assignedUsers = useMemo(
    () =>
      selectedStoreAssignments
        .map((item) => users.find((user) => user.id === item.userId))
        .filter((item): item is StoreAssignmentBoardUserItem => !!item),
    [selectedStoreAssignments, users],
  );

  const sourceUsers = useMemo(
    () => users.filter((user) => !selectedStoreUserIds.has(user.id)),
    [users, selectedStoreUserIds],
  );

  const handleDragStart = (userId: string) => (event: DragEvent<HTMLDivElement>) => {
    event.dataTransfer.setData('userId', userId);
    setDraggingUserId(userId);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
  };

  const handleDropToAssigned = async (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    const userId = event.dataTransfer.getData('userId');
    setDraggingUserId(null);
    if (!userId || !selectedStore) return;
    await onAssign({ userId });
  };

  const handleDropToSource = async (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    const userId = event.dataTransfer.getData('userId');
    setDraggingUserId(null);
    if (!userId || !selectedStore) return;
    if (selectedStoreUserIds.has(userId)) {
      await onUnassign(userId);
    }
  };

  const renderUserCard = (user: StoreAssignmentBoardUserItem) => {
    const userAssignments = assignmentsByUserId.get(user.id) ?? [];
    const isOtherStoreAssigned =
      selectedStore &&
      userAssignments.some((assignment) => assignment.storeId !== selectedStore.id);

    return (
      <div
        key={user.id}
        draggable={!isSaving}
        onDragStart={handleDragStart(user.id)}
        onDragEnd={() => setDraggingUserId(null)}
        className={`bg-white border border-gray-200 rounded-lg p-3 shadow-sm cursor-move hover:border-indigo-300 transition-all ${
          draggingUserId === user.id ? 'opacity-60' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate">{user.name}</p>
            <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
            <p className="text-[11px] text-gray-500 mt-1">
              {user.roleName}
              {user.positionName ? ` / ${user.positionName}` : ''}
            </p>
            {isOtherStoreAssigned ? (
              <p className="text-[11px] text-amber-600 mt-1">他店舗に配属中（移動可）</p>
            ) : null}
          </div>
          <GripVertical size={14} className="text-gray-300 shrink-0" />
        </div>
      </div>
    );
  };

  const renderDropLane = (
    title: string,
    icon: ReactNode,
    usersInLane: StoreAssignmentBoardUserItem[],
  ) => (
    <section
      onDragOver={handleDragOver}
      onDrop={handleDropToAssigned}
      className="border border-gray-200 rounded-xl overflow-hidden bg-white"
    >
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2">
          {icon}
          {title}
        </h4>
        <span className="text-xs text-gray-500 px-2 py-0.5 rounded-full border border-gray-200 bg-white">
          {usersInLane.length}名
        </span>
      </div>
      <div className="p-3 min-h-[180px]">
        <div className="space-y-2">
          {usersInLane.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">ここにドロップ</p>
          ) : (
            usersInLane.map(renderUserCard)
          )}
        </div>
      </div>
    </section>
  );

  if (!isOpen || !selectedStore) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl max-h-[90vh] bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
              <Building2 size={18} className="text-indigo-500" />
              {selectedStore.storeName} 配属設定
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              左のユーザー一覧から、右の配属枠へドラッグして配属してください。
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white rounded-md transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 sm:p-5 overflow-auto">
          {errorMessage ? <p className="text-xs text-red-500 mb-4">{errorMessage}</p> : null}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <section
              onDragOver={handleDragOver}
              onDrop={handleDropToSource}
              className="border-2 border-dashed border-gray-300 rounded-xl p-4 bg-gray-50 min-h-[420px]"
            >
              <h4 className="text-sm font-bold text-gray-700 flex items-center gap-2 mb-3">
                <User size={15} className="text-indigo-500" /> ユーザー一覧
              </h4>
              <p className="text-[11px] text-gray-500 mb-3">この枠に戻すと対象店舗から配属解除されます。</p>
              <div className="space-y-2">
                {sourceUsers.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-8">表示ユーザーなし</p>
                ) : (
                  sourceUsers.map(renderUserCard)
                )}
              </div>
            </section>

            <section className="grid grid-cols-1 gap-4">
              {renderDropLane(
                '配属ユーザー',
                <User size={15} className="text-indigo-500" />,
                assignedUsers,
              )}
            </section>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onDeleteStore}
              disabled={isSaving || !canDeleteStore}
              className="px-4 py-2 text-sm font-bold rounded-md border border-red-200 text-red-500 bg-white hover:bg-red-50 disabled:opacity-40"
            >
              店舗を削除
            </button>
            {!canDeleteStore ? (
              <p className="text-xs text-gray-500">配属ユーザーがいる間は削除できません。</p>
            ) : (
              <p className="text-xs text-gray-500">配属操作は即時保存されます。</p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-bold rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 disabled:opacity-50"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}
